"use client";

import { useState } from "react";
import { PlusCircle, MinusCircle } from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function AddFacultyForm({
  onSuccess,
  onCancel,
  isLoading,
  setIsLoading,
  buttonSpinner,
}) {
  const roleHierarchy = [
    "Associate Professor / Head of Department",
    "Assistant Professor / Head of department",
    "Head of Department",
    "Associate Professor",
    "Assistant Professor",
    "Lecturer",
    "Lab Incharge",
    "Lab Engineer",
    "Lab Assistant",
  ];

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    department: "",
    role: "",
    bio: "",
    googleScholar: "",
    linkedinProfile: "",
    roomNumber: "",
    joiningDate: "",
    image: null,
    education: [""],
    articles: [{ title: "", link: "" }],
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const form = new FormData();

    Object.keys(formData).forEach((key) => {
      if (key === "education") {
        const filteredEducation = formData.education.filter((edu) =>
          edu.trim()
        );
        form.append("education", JSON.stringify(filteredEducation));
      } else if (key === "articles") {
        const filteredArticles = formData.articles.filter(
          (article) => article.title.trim() && article.link.trim()
        );
        form.append("articles", JSON.stringify(filteredArticles));
      } else if (key === "image" && formData[key]) {
        form.append("image", formData[key]);
      } else {
        form.append(key, formData[key]);
      }
    });

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/faculty/add-faculty`,
        {
          method: "POST",
          body: form,
        }
      );

      if (response.ok) {
        onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.message || "Failed to add faculty member");
      }
    } catch (error) {
      console.error("Error adding faculty member:", error);
      toast.error("Failed to add faculty member");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "image" && files) {
      setFormData((prev) => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleEducationChange = (index, value) => {
    const newEducation = [...formData.education];
    newEducation[index] = value;
    setFormData((prev) => ({ ...prev, education: newEducation }));
  };

  const handleArticleChange = (index, field, value) => {
    const newArticles = [...formData.articles];
    newArticles[index] = { ...newArticles[index], [field]: value };
    setFormData((prev) => ({ ...prev, articles: newArticles }));
  };

  const addEducationField = () => {
    setFormData((prev) => ({
      ...prev,
      education: [...prev.education, ""],
    }));
  };

  const addArticleField = () => {
    setFormData((prev) => ({
      ...prev,
      articles: [...prev.articles, { title: "", link: "" }],
    }));
  };

  const removeEducationField = (index) => {
    const newEducation = formData.education.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, education: newEducation }));
  };

  const removeArticleField = (index) => {
    const newArticles = formData.articles.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, articles: newArticles }));
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white shadow-md rounded-lg p-8 max-w-4xl mx-auto"
    >
      <div style={{}} className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">
            Basic Information
          </h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Department
            </label>
            <select
              name="department"
              value={formData.department}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Department</option>
              <option value="CS">Computer Science</option>
              <option value="AI">Artificial Intelligence</option>
              <option value="CyS">Cyber Security</option>
              <option value="SE">Software Engineering</option>
              <option value="H&S">Basic Sciences & Humanities</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Role</option>
              {roleHierarchy.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Additional Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">
            Additional Information
          </h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Google Scholar
            </label>
            <input
              type="url"
              name="googleScholar"
              value={formData.googleScholar}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              LinkedIn Profile
            </label>
            <input
              type="url"
              name="linkedinProfile"
              value={formData.linkedinProfile}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="https://www.linkedin.com/in/username"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Profile Image
            </label>
            <input
              type="file"
              name="image"
              accept="image/*"
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bio
            </label>
            <textarea
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Education Fields */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-700">Education</h3>
          <button
            type="button"
            onClick={addEducationField}
            className="text-blue-600 hover:text-blue-800 flex items-center"
          >
            <PlusCircle className="w-5 h-5 mr-1" />
            Add Education
          </button>
        </div>
        {formData.education.map((edu, index) => (
          <div key={index} className="flex items-center gap-2 mb-2">
            <input
              type="text"
              value={edu}
              onChange={(e) => handleEducationChange(index, e.target.value)}
              placeholder="e.g., Ph.D. in Computer Science, MIT"
              className="flex-grow px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {formData.education.length > 1 && (
              <button
                type="button"
                onClick={() => removeEducationField(index)}
                className="text-red-600 hover:text-red-800"
              >
                <MinusCircle className="w-5 h-5" />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Articles Fields */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-700">Articles</h3>
          <button
            type="button"
            onClick={addArticleField}
            className="text-blue-600 hover:text-blue-800 flex items-center"
          >
            <PlusCircle className="w-5 h-5 mr-1" />
            Add Article
          </button>
        </div>
        {formData.articles.map((article, index) => (
          <div
            key={index}
            className="mb-4 p-4 border border-gray-200 rounded-md bg-gray-50"
          >
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={article.title}
                onChange={(e) =>
                  handleArticleChange(index, "title", e.target.value)
                }
                placeholder="Article Title"
                className="flex-grow px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {formData.articles.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeArticleField(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  <MinusCircle className="w-5 h-5" />
                </button>
              )}
            </div>
            <input
              type="url"
              value={article.link}
              onChange={(e) =>
                handleArticleChange(index, "link", e.target.value)
              }
              placeholder="Article Link"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-4 mt-6">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className={`px-4 py-2 rounded-md flex items-center justify-center transition-colors duration-300 ${
            isLoading
              ? "bg-blue-400 cursor-wait"
              : "bg-blue-600 hover:bg-blue-700 text-white"
          }`}
        >
          {isLoading ? <>{buttonSpinner} Adding...</> : "Add Faculty Member"}
        </button>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
      />
    </form>
  );
}
